import { LocalesValue, XcoreConfigEnvironmentValue, XcoreConfigRawValue } from ".";
import { ConfigError } from "./errors";
import { readXcoreConfig } from "./read";

type XcoreConfigRawPartialEnvs = Record<string, Partial<XcoreConfigEnvironmentValue>>;

/**
 * @throws {ConfigError}
 */
export const validateRawConfig = (value: Partial<XcoreConfigRawValue>) => {
  // Prevent throwing errors in production
  if (process.env.NODE_ENV === "production") {
    return;
  }

  isset(value, "defaultLocale", "string");
  isset(value, "admin", "object");
  isset(value, "env", "object");

  Object.keys(value.env as XcoreConfigRawPartialEnvs).forEach((env) =>
    validateConfig((value.env as XcoreConfigRawPartialEnvs)[env], value.defaultLocale)
  );
};

/**
 * @throws {ConfigError}
 */
export const validateConfig = (
  value: Partial<XcoreConfigEnvironmentValue> & { defaultLocale?: string },
  defaultLocaleProvided?: string
) => {
  // Prevent throwing errors in production
  if (process.env.NODE_ENV === "production") {
    return;
  }

  isset(value, "locales", "object");

  isset(value, "admin", "object");

  value.revalidate && isset(value, "revalidate", "number");

  const defaultLocale = defaultLocaleProvided ?? value?.defaultLocale;

  if (!defaultLocale) {
    throw new ConfigError("Default locale not provided.");
  }

  const configLocales = Object.keys(value.locales as Record<string, LocalesValue>);

  if (!configLocales.includes(defaultLocale)) {
    throw new ConfigError("Default locale must be one of the specified locales.");
  }

  configLocales.forEach((locale: string) => {
    if (value?.locales?.[locale]?.domains?.some(({ domain }) => domain.includes(":"))) {
      throw new ConfigError("Domains should not include ports.");
    }
  });
};

/**
 * @throws {ConfigError}
 */
const isset = <T extends object>(
  v: T,
  field: keyof T & string,
  _typeof?: "bigint" | "boolean" | "number" | "object" | "string" | "symbol" | "undefined",
  required = true
) => {
  if (!required && !(field in v)) {
    return;
  }

  if (!(field in v)) {
    throw new ConfigError(`Missing ${field} field.`);
  }

  if (_typeof && typeof v[field] !== _typeof) {
    const article = _typeof.startsWith("o") || _typeof.startsWith("u") ? "an" : "a";
    throw new ConfigError(`Field ${field} is not ${article} ${_typeof} (${String(v[field])}).`);
  }
};

/**
 * @throws {ConfigError}
 */
export const readAndValidateRawXcoreConfig = async (): Promise<string> => {
  try {
    const configRaw = await readXcoreConfig();

    validateRawConfig(configRaw);

    return "\n✅ xcore.config.json is valid\n";
  } catch (e: unknown) {
    if (e instanceof ConfigError) {
      return `\n ❌ ${String(e.message)}\n`;
    }

    return `\n ❌ ${String(e)}\n`;
  }
};
